import { useMsal } from "@azure/msal-react";
import { FilterAlt } from "@mui/icons-material";
import { Card, CardContent, Grid, Radio, Switch, Typography } from "@mui/material/";
import { cloneDeep } from "lodash";
import { useContext, useEffect, useState } from "react";
import AnimateHeight from "react-animate-height";
import { PermissionContext } from "../../contexts/PermissionContext";
import { SearchContext } from "../../contexts/SearchContext";
import { ISearchResultOption } from "../../interfaces";
import { Role } from "../../utils";
import { SearchFilterDialog } from "../SearchFilterDialog";
import './index.scss';
import { WidgetHeader } from "./WidgetHeader";


const ResultOptionRow = (opt: ISearchResultOption, onToggleVisible: () => void, onOrderBy: () => void, onUpdate: () => void) => {

    const disabled = ["l1", "l2", "l3"];

    return <Grid item xs={12} container key={opt.propertyName} className="result-option-row">
        <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>{opt.label}</Grid>
        <Grid item xs={2}><SearchFilterDialog {...opt} /></Grid>
        <Grid item xs={2}><Radio checked={opt.orderBy} onChange={onOrderBy} /></Grid>
        <Grid item xs={2}><Switch checked={opt.visible} onChange={onToggleVisible} disabled={disabled.includes(opt.propertyName)} /> </Grid>
    </Grid>
}

const DefaultResultOptionsModel: ISearchResultOption[] = [{
    propertyName: "spareType",
    label: "Spare Type",
    orderBy: false,
    visible: true,
},
{
    propertyName: "vehicleClasses",
    label: "Vehicle Class",
    orderBy: false,
    visible: true,
},
{
    propertyName: "l1",
    label: "Description Level 1",
    orderBy: false,
    visible: true,
},
{
    propertyName: "l2",
    label: "Description Level 2",
    orderBy: false,
    visible: true,
},
{
    propertyName: "l3",
    label: "Description Level 3",
    orderBy: true, //default
    visible: true,
},
{
    propertyName: "partNumber",
    label: "Part Number",
    orderBy: false,
    visible: true,
},
{
    propertyName: "catalogueNumber",
    label: "Catalogue Number",
    orderBy: false,
    visible: true,
},
{
    propertyName: "manufacturerNumber",
    label: "Manufacturer Number",
    orderBy: false,
    visible: true,
},
{
    propertyName: "serialNumber",
    label: "Serial Number",
    orderBy: false,
    visible: false,
},
{
    propertyName: "vehicleNumber",
    label: "Vehicle Number",
    orderBy: false,
    visible: false,
},
{
    propertyName: "unitNumber",
    label: "Unit Number",
    orderBy: false,
    visible: false,
},
{
    propertyName: "porterbrookAssetNumber",
    label: "Porterbrook Asset Number",
    orderBy: false,
    visible: true,
},
{
    propertyName: "quantity",
    label: "Total / Qty",
    orderBy: false,
    visible: true,
},
{
    propertyName: "site",
    label: "Site",
    orderBy: false,
    visible: true,
},
{
    propertyName: "state",
    label: "Status",
    orderBy: false,
    visible: true,
},
{
    propertyName: "warehouse",
    label: "Warehouse",
    orderBy: false,
    visible: false,
},
{
    propertyName: "rack",
    label: "Rack",
    orderBy: false,
    visible: false,
},
{
    propertyName: "row",
    label: "Row",
    orderBy: false,
    visible: false,
},
{
    propertyName: "pallet",
    label: "Pallet Number",
    orderBy: false,
    visible: false,
},
{
    propertyName: "ownedBy",
    label: "Owned By",
    orderBy: false,
    visible: true,
},
{
    propertyName: "hasUsageRestrictions",
    label: "Has Restrictions",
    orderBy: false,
    visible: true,
},

{
    propertyName: "valueEstimate",
    label: "Value Estimate",
    orderBy: false,
    visible: true,
},
{
    propertyName: "totalValueEstimate",
    label: "Value Estimate (Total)",
    orderBy: false,
    visible: true,
},

{
    propertyName: "weightEstimate",
    label: "Weight Estimate",
    orderBy: false,
    visible: true,
},
{
    propertyName: "arrivalDate",
    label: "Arrival Date",
    orderBy: false,
    visible: false,
},
{
    propertyName: "partExpiry",
    label: "Part Expiry",
    orderBy: false,
    visible: false,
},
{
    propertyName: "contractReference",
    label: "Contract Ref",
    orderBy: false,
    visible: true,
},
{
    propertyName: "storageRequestReference",
    label: "Storage Request Ref",
    orderBy: false,
    visible: true,
},

{
    propertyName: "paperWorkReceived",
    label: "Paperwork Ref",
    orderBy: false,
    visible: false,
},
{
    propertyName: "notes",
    label: "Notes",
    orderBy: false,
    visible: false,
},
{
    propertyName: "responsibleEngineerAtLastAudit",
    label: "Responsible Engineer",
    orderBy: false,
    visible: false,
    },
    {
        propertyName: "storageReason",
        label: "Storage Reason",
        orderBy: false,
        visible: false,
    },
    {
        propertyName: "retentionDecision",
        label: "Retention Decision",
        orderBy: false,
        visible: false,
    }
];

const InternalOnly = [
    "spareType",
    "warehouse",
    "rack",
    "row",
    "valueEstimate",
    "valueOrigin",
    "notes"
];






export const ResultOptionsWidget: React.FC = ({
    ...props
}) => {

    const searchContext = useContext(SearchContext);
    const permissionContext = useContext(PermissionContext);

    const [expanded, setExpanded] = useState<boolean>(false);

    const onToggleVisible = (option: ISearchResultOption) => {
        let newOptions = cloneDeep(searchContext.resultOptions);

        newOptions.forEach(x => {
            if (x.propertyName === option.propertyName)
                x.visible = !x.visible;
        });

        searchContext.setResultOptions(newOptions);

    }

    const onOrderBy = (option: ISearchResultOption) => {
        let newOptions = cloneDeep(searchContext.resultOptions);

        newOptions.forEach(x => {
            if (x.propertyName === option.propertyName)
                x.orderBy = true;
            else x.orderBy = false;
        });

        searchContext.setResultOptions(newOptions);

    }

    const onUpdate = (option: ISearchResultOption) => {
        let newOptions = cloneDeep(searchContext.resultOptions);

        newOptions.forEach(x => {
            if (x.propertyName === option.propertyName)
                x.filteredValues = option.filteredValues;
        });

        searchContext.setResultOptions(newOptions);

    }

    const userIsExternal = () => {


        return permissionContext.isRole(Role.External);

    }

    const { accounts } = useMsal();

    useEffect(() => {

        let accountId = accounts[0] && accounts[0].localAccountId;
        let userOptionsJson = window.localStorage.getItem("resultoptions_" + accountId);
        let userResultOptions = JSON.parse(userOptionsJson ?? "[]") as ISearchResultOption[];

        let emptyState = searchContext.resultOptions.length === 0;


        //check that the available options have not changed.

        let userOptionsValid = true;
        DefaultResultOptionsModel.every(option => {
            if (!userResultOptions.map(x => x.propertyName).includes(option.propertyName)) {
                userOptionsValid = false;
                return false;
            } else return true;
        });
        userOptionsValid = userOptionsValid ?
            userResultOptions.length === DefaultResultOptionsModel.length :
            userOptionsValid;



        if (userOptionsValid && emptyState) {                           //the user has stored preferences           
            searchContext.setResultOptions(userResultOptions);
        } else if (emptyState) {                                        //the user has no stored preferences 
            searchContext.setResultOptions(DefaultResultOptionsModel);
            window.localStorage.setItem("resultoptions_" + accountId,
                JSON.stringify(DefaultResultOptionsModel));
        } else {                                                        //store user preferences

            let opts = cloneDeep(searchContext.resultOptions);

            //dont persist filters and ordering 
            opts.forEach(x => {
                x.filteredValues = [];
                x.orderBy = x.propertyName === "l3" ? true : false;
            });

            window.localStorage.setItem("resultoptions_" + accountId,
                JSON.stringify(opts));
        }


    }, [searchContext.resultOptions])// eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Card elevation={3} className="dashboard-widget" sx={{ height: "auto" }}>
            <WidgetHeader><FilterAlt sx={{ mr: 1 }} /> Result Options
                <Switch
                    sx={{ float: "right" }}
                    size="small"
                    color="secondary"
                    checked={expanded}
                    onChange={() => setExpanded(!expanded)}
                />
            </WidgetHeader>
            <AnimateHeight height={expanded ? "auto" : 0} duration={500}>

                <CardContent sx={{ maxHeight: "50vh", overflowY: "auto", scrollbarWidth: "thin" }}>
                    <Grid item xs={12} container>
                        <Grid item xs={6} />
                        <Grid item xs={2}><Typography variant="subtitle2" color="primary">Filter</Typography></Grid>
                        <Grid item xs={2}><Typography variant="subtitle2" color="primary">Order</Typography></Grid>
                        <Grid item xs={2}><Typography variant="subtitle2" color="primary">Enabled</Typography></Grid>
                    </Grid>

                    {searchContext.resultOptions
                        .sort((a, b) => (a.label > b.label) ? 1 : -1)
                        .filter((opt) => {

                            //hide options for restricted properties
                            if (userIsExternal())
                                if (InternalOnly.includes(opt.propertyName))
                                    return false;

                            return opt;
                        })
                        .map((opt) => ResultOptionRow(opt,
                            () => onToggleVisible(opt),
                            () => onOrderBy(opt),
                            () => onUpdate(opt)
                        ))}

                </CardContent>
            </AnimateHeight>
        </Card >
    );

}